import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/tools/ToolShell";
import { DE_TOOLS, DE_CATEGORIES } from "@/lib/de-tools-catalog";
import { DE_PDF_TOOLS } from "@/lib/de-pdf-catalog";
import { PicaminiLogo } from "@/components/ui/Logo";
import {
  Shuffle,
  Disc3,
  Users,
  Timer,
  FileText,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  Zap,
  Globe,
} from "lucide-react";

export const Route = createFileRoute("/de/")({
  head: () => ({
    meta: [
      { title: "Kostenlose Unterrichts-Tools, Zufallsräder & PDF-Werkzeuge | Picamini.online" },
      {
        name: "description",
        content:
          "Kostenlose Online-Tools für Unterricht & Entscheidungen: Zufallsrad Namensauswahl, Gruppen-Generator, Schultimer und 100% private PDF-Werkzeuge ohne Registrierung.",
      },
      {
        name: "keywords",
        content:
          "picamini, zufallsgenerator namen, glücksrad namensauswahl, zufallsrad online, gruppen generator, schultimer, lehrer tools kostenlos, pdf zusammenfügen kostenlos, pdf bearbeiten online",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "Kostenlose Unterrichts-Tools & Glücksräder | Picamini.online" },
      {
        property: "og:description",
        content:
          "37+ interaktive Online-Tools für Lehrkräfte und Moderatoren: Zufallsräder, Gruppen-Generatoren, Schultimer und PDF-Werkzeuge.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/de/" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { property: "og:locale", content: "de_DE" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Kostenlose Unterrichts-Tools auf Deutsch | Picamini.online" },
      { name: "twitter:description", content: "37+ kostenlose Online-Tools für Schule, Unterricht und Events." },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/de/" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/" },
      { rel: "alternate", hrefLang: "es", href: "https://picamini.online/es/" },
      { rel: "alternate", hrefLang: "de", href: "https://picamini.online/de/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Picamini.online Deutsch",
          "url": "https://picamini.online/de/",
          "inLanguage": "de",
          "description": "Kostenlose digitale Unterrichtshilfen, Zufallsgeneratoren und private PDF-Werkzeuge direkt im Browser.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Beliebte Unterrichts- & Entscheidungstools",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Zufalls-Namensgenerator",
              "url": "https://picamini.online/de/zufalls-namensgenerator",
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Glücksrad Online",
              "url": "https://picamini.online/de/gluecksrad-online",
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Glücksrad Namensauswahl",
              "url": "https://picamini.online/de/gluecksrad-namensauswahl",
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Gruppen-Generator Zufall",
              "url": "https://picamini.online/de/gruppen-generator-zufall",
            },
            {
              "@type": "ListItem",
              "position": 5,
              "name": "Schultimer für den Unterricht",
              "url": "https://picamini.online/de/schultimer-unterricht",
            },
            {
              "@type": "ListItem",
              "position": 6,
              "name": "PDF Werkzeuge Hub",
              "url": "https://picamini.online/de/pdf-tools",
            },
          ],
        }),
      },
    ],
  }),
  component: DeHomepage,
});

function DeHomepage() {
  const featuredTools = DE_TOOLS.filter((t) => t.featured);
  const featuredPdfTools = DE_PDF_TOOLS.filter((t) => t.featured);

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      <SiteHeader lang="de" />

      <main className="flex-1">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background py-16 sm:py-24 border-b border-border/50">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 text-center space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5 text-xs sm:text-sm font-bold text-primary">
              <Sparkles className="size-4" /> 100% Kostenlos • Ohne Registrierung • Voller Datenschutz
            </div>

            <h1 className="text-4xl font-extrabold tracking-tight sm:text-6xl text-foreground max-w-4xl mx-auto leading-tight">
              Kostenlose Tools für die Schule, <span className="text-primary">Zufallsräder</span> & PDF-Werkzeuge
            </h1>

            <p className="max-w-3xl mx-auto text-base sm:text-xl text-muted-foreground leading-relaxed">
              Entwickelt für Lehrkräfte, Schüler und Moderatoren. Schülernamen zufällig auslosen, Glücksräder drehen, faire Gruppen bilden und PDFs direkt im Browser bearbeiten.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
              <a
                href="#werkzeuge"
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-base font-bold text-primary-foreground shadow-lg transition-transform hover:scale-105"
              >
                Tools erkunden <ArrowRight className="size-5" />
              </a>
              <Link
                to="/de/pdf-tools"
                className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-6 py-3.5 text-base font-bold text-foreground transition-colors hover:bg-secondary"
              >
                <FileText className="size-5 text-primary" /> PDF-Werkzeuge
              </Link>
            </div>
          </div>
        </section>

        {/* FEATURED TOOLS GRID */}
        <section id="werkzeuge" className="py-16 mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center space-y-3">
            <h2 className="text-3xl font-extrabold sm:text-4xl text-foreground">
              Beliebteste Online-Tools
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              Sofort einsatzbereite interaktive Helfer für Unterricht, Seminare und Events.
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {featuredTools.map((tool) => (
              <Link
                key={tool.slug}
                to="/de/$slug"
                params={{ slug: tool.slug }}
                className="surface-card p-6 rounded-2xl border transition-all hover:-translate-y-1 hover:shadow-lg flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Disc3 className="size-5" />
                    </span>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground px-2 py-0.5 rounded-md bg-secondary">
                      {tool.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-foreground">{tool.name}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{tool.tagline}</p>
                </div>
                <div className="pt-4 flex items-center gap-1.5 text-xs font-bold text-primary">
                  Gratis ausprobieren <ArrowRight className="size-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* ALL CATEGORIES */}
        {DE_CATEGORIES.map((cat) => {
          const categoryTools = DE_TOOLS.filter((t) => t.category === cat);
          if (categoryTools.length === 0) return null;

          return (
            <section key={cat} className="py-10 border-t border-border/40 mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-6">
              <h3 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <span className="size-3 rounded-full bg-primary" /> {cat}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {categoryTools.map((tool) => (
                  <Link
                    key={tool.slug}
                    to="/de/$slug"
                    params={{ slug: tool.slug }}
                    className="p-4 rounded-xl border bg-card transition-all hover:bg-secondary/40 hover:-translate-y-0.5 space-y-1.5"
                  >
                    <p className="font-bold text-sm text-foreground">{tool.name}</p>
                    <p className="text-xs text-muted-foreground leading-snug line-clamp-2">{tool.tagline}</p>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}

        {/* PDF TOOLS PREVIEW */}
        <section className="py-16 bg-secondary/30 border-y border-border/50">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-10">
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary">PDF Toolkit Pro</span>
                <h2 className="text-3xl font-extrabold sm:text-4xl text-foreground mt-1">
                  100% Private PDF-Werkzeuge im Browser
                </h2>
                <p className="text-base text-muted-foreground mt-2 max-w-2xl">
                  PDFs zusammenfügen, teilen, komprimieren und bearbeiten — ohne Upload auf fremde Server.
                </p>
              </div>
              <Link
                to="/de/pdf-tools"
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"
              >
                Alle PDF-Tools ansehen <ArrowRight className="size-4" />
              </Link>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {featuredPdfTools.slice(0, 6).map((pdfTool) => (
                <Link
                  key={pdfTool.slug}
                  to="/de/pdf-tools/$slug"
                  params={{ slug: pdfTool.slug }}
                  className="surface-card p-6 rounded-2xl border bg-card transition-all hover:-translate-y-1 hover:shadow-md space-y-3"
                >
                  <div className="flex items-center gap-3">
                    <span className="grid size-10 place-items-center rounded-xl bg-primary/10 text-primary">
                      <FileText className="size-5" />
                    </span>
                    <div>
                      <h3 className="font-bold text-base text-foreground">{pdfTool.name}</h3>
                      <span className="text-[11px] text-muted-foreground">{pdfTool.category}</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{pdfTool.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* WHY PICAMINI */}
        <section className="py-16 mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center space-y-3">
            <h2 className="text-3xl font-extrabold text-foreground">Warum Picamini.online nutzen?</h2>
            <p className="text-base text-muted-foreground max-w-xl mx-auto">
              Optimiert für Datenschutz, Geschwindigkeit und intuitive Handhabung im Unterricht.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            <div className="p-6 rounded-2xl border bg-card space-y-3">
              <span className="grid size-12 place-items-center rounded-2xl bg-emerald-500/10 text-emerald-500 font-bold">
                <ShieldCheck className="size-6" />
              </span>
              <h3 className="text-lg font-bold text-foreground">100% Datenschutzkonform</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Namen und PDF-Dateien werden niemals auf externe Server übertragen. Die Verarbeitung erfolgt vollständig lokal in Ihrem Browser.
              </p>
            </div>

            <div className="p-6 rounded-2xl border bg-card space-y-3">
              <span className="grid size-12 place-items-center rounded-2xl bg-blue-500/10 text-blue-500 font-bold">
                <Zap className="size-6" />
              </span>
              <h3 className="text-lg font-bold text-foreground">Ohne Anmeldung & Kosten</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Keine Registrierung, keine Passwörter und keine versteckten Gebühren. Direkt aufrufen und sofort einsetzen.
              </p>
            </div>

            <div className="p-6 rounded-2xl border bg-card space-y-3">
              <span className="grid size-12 place-items-center rounded-2xl bg-purple-500/10 text-purple-500 font-bold">
                <Globe className="size-6" />
              </span>
              <h3 className="text-lg font-bold text-foreground">Ideal für Smartboards</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Hohe Kontraste und flüssige Animationen sorgen für eine perfekte Projektion am interaktiven Whiteboard oder Beamer.
              </p>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter lang="de" />
    </div>
  );
}
